// Custom Solana WalletConnect adapter (#57) built directly on @walletconnect/universal-provider.
//
// WHY a bespoke adapter (and NOT @reown/appkit / @walletconnect/solana-adapter):
// those packages drag in AppKit's modal + a version-skewed WalletConnect core, which conflicts with the
// wagmi-managed WalletConnect core already in the tree and re-introduces the `window.solana` provider
// contention that main.tsx deliberately avoids (its `solanaWallets` array is intentionally empty). A
// UniversalProvider-based adapter uses a SEPARATE transport (relay/websocket + QR/deep-link), so it does
// NOT touch injected `window.solana` and is safe to register alongside the wagmi/injected wallets.
//
// ── SERIALIZATION CONTRACT (the security-critical part — every assumption is documented) ──
// Source of truth: Reown/WalletConnect Solana JSON-RPC reference
//   • https://docs.reown.com/advanced/multichain/rpc-reference/solana-rpc
//   • reference impl: @reown/appkit-utils SolanaWalletConnectStandardWallet (read, not imported)
//
//   solana_signTransaction
//     request  params: { transaction: <base64 serialized tx>, pubkey: <base58 address> }
//     response: { signature: <base58> }        (legacy/older wallets — apply sig onto our tx), OR
//               { transaction: <base64 signed> } (newer wallets — return the wallet's signed tx)
//   solana_signMessage
//     request  params: { message: <base58 encoded message>, pubkey: <base58 address> }
//     response: { signature: <base58> }         (decode to raw 64-byte Uint8Array)
//   solana_signAllTransactions (used opportunistically; we fall back to per-tx signing)
//     request  params: { transactions: <base64[]> }
//     response: { transactions: <base64[]> }
//
// DEVICE-TEST-REQUIRED: the exact request/response encodings above, and the full round-trip against a
// real wallet (Phantom / Solflare / Backpack) over the WC relay, are NOT verifiable in CI (no live relay,
// no wallet). A reviewer MUST confirm on-device that a signed transaction produced through this adapter
// broadcasts successfully before this path handles real funds. See the per-method DEVICE-TEST notes below.

import {
  BaseMessageSignerWalletAdapter,
  WalletReadyState,
  WalletConnectionError,
  WalletDisconnectionError,
  WalletNotConnectedError,
  WalletPublicKeyError,
  WalletSignTransactionError,
  WalletSignMessageError,
  isVersionedTransaction,
  type WalletName,
  type SupportedTransactionVersions,
} from '@solana/wallet-adapter-base'
import { PublicKey, Transaction, VersionedTransaction } from '@solana/web3.js'
import bs58 from 'bs58'
import { Buffer } from 'buffer'
import type UniversalProvider from '@walletconnect/universal-provider'

export const WalletConnectSolanaWalletName = 'WalletConnect' as WalletName<'WalletConnect'>

// CAIP-2 chain references (the part after `solana:`). These are the FIRST 32 bytes of the base58
// genesis hash of each cluster, per the CAIP-2 Solana namespace / WalletConnect chain registry.
//   mainnet-beta genesis: 5eykt4UsFv8P8NJdTREpY1vzqKqZKvdpTHby4h4egEm → truncated ref below
//   devnet       genesis: EtWTRABZaYq6iMfeYKouRu166VU2xqa1cXeTdSw6y53z → truncated ref below
// Verified against Reown's own map (appkit-utils SolanaWalletConnectStandardWallet caipNetworkToStandardChain)
// and https://docs.reown.com/advanced/multichain/rpc-reference/solana-rpc.
export const SOLANA_CAIP2 = {
  mainnet: 'solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp',
  devnet: 'solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1',
} as const

// The WC RPC methods we request in the session namespace. Kept to exactly what the app needs.
const SOLANA_METHODS = ['solana_signTransaction', 'solana_signMessage'] as const

export type WcSolanaNetwork = 'mainnet' | 'testnet'

export interface WalletConnectSolanaAdapterConfig {
  projectId: string
  /** App network. 'mainnet' → Solana mainnet-beta CAIP-2; anything else → devnet (matches main.tsx). */
  network: WcSolanaNetwork | string | undefined
  /** Optional: called with the WC pairing URI so a QR/deep-link UI can render it. */
  onDisplayUri?: (uri: string) => void
  /** Injected for tests — a factory returning a UniversalProvider-like instance. */
  providerFactory?: (opts: { projectId: string; metadata: WcMetadata }) => Promise<UniversalProvider>
}

interface WcMetadata {
  name: string
  description: string
  url: string
  icons: string[]
}

// The narrow slice of UniversalProvider this adapter depends on. Declared explicitly so the unit tests can
// supply a faithful mock without pulling the real relay/websocket stack.
export interface WcProviderLike {
  session?: {
    namespaces?: Record<string, { accounts?: string[] } | undefined>
  }
  on(event: string, listener: (...args: unknown[]) => void): void
  off?(event: string, listener: (...args: unknown[]) => void): void
  connect(opts: unknown): Promise<unknown>
  disconnect(): Promise<void>
  request<T = unknown>(args: { method: string; params?: unknown }, chain?: string): Promise<T>
}

function resolveChainId(network: string | undefined): string {
  return network === 'mainnet' ? SOLANA_CAIP2.mainnet : SOLANA_CAIP2.devnet
}

// Parse the base58 address out of a CAIP-10 account id: `solana:<chainRef>:<base58Address>`.
// A base58 address never contains ':', so the address is everything after the LAST ':'.
export function parseSolanaAddressFromCaip(account: string): string {
  const idx = account.lastIndexOf(':')
  const address = idx >= 0 ? account.slice(idx + 1) : account
  if (!address) throw new WalletPublicKeyError(`Malformed WalletConnect Solana account: "${account}"`)
  return address
}

export class WalletConnectSolanaAdapter extends BaseMessageSignerWalletAdapter {
  name = WalletConnectSolanaWalletName
  url = 'https://walletconnect.network'
  // WalletConnect logo (data URI) — the base class `icon` must be a self-contained string; the modal
  // separately renders the shared WalletLogos['walletconnect'] SVG keyed on the adapter name.
  icon =
    'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0MDAgNDAwIj48Y2lyY2xlIGN4PSIyMDAiIGN5PSIyMDAiIHI9IjIwMCIgZmlsbD0iIzNCOTlGQyIvPjxwYXRoIGZpbGw9IiNmZmYiIGQ9Ik0xMjIuNTIgMTQ4Ljk3YzQzLjAxLTQxLjk5IDExMi45NS00MS45OSAxNTUuOTYgMGw1LjE4IDUuMDVjMi4xNSAyLjEgMi4xNSA1LjUxIDAgNy42MWwtMTcuNzIgMTcuMjljLTEuMDcgMS4wNS0yLjgyIDEuMDUtMy44OSAwbC03LjEyLTYuOTVjLTMwLjAxLTI5LjMtNzguODUtMjkuMy0xMDguODYgMGwtNy42MyA3LjQ0Yy0xLjA3IDEuMDUtMi44MiAxLjA1LTMuODkgMGwtMTcuNzItMTcuMjljLTIuMTUtMi4xLTIuMTUtNS41MSAwLTcuNjFsNS42OC01LjU1Wk0zMTUuMTEgMTg0LjczbDE1Ljc3IDE1LjRjMi4xNSAyLjEgMi4xNSA1LjUxIDAgNy42MWwtNzEuMDcgNjkuMzhjLTIuMTUgMi4xLTUuNjQgMi4xLTcuNzkgMGwtNTAuNDQtNDkuMjRhLjk3Ljk3IDAgMCAwLTEuMzYgMGwtNTAuNDQgNDkuMjRjLTIuMTUgMi4xLTUuNjQgMi4xLTcuNzkgMGwtNzEuMDctNjkuMzhjLTIuMTUtMi4xLTIuMTUtNS41MSAwLTcuNjFsMTUuNzctMTUuNGMyLjE1LTIuMSA1LjY0LTIuMSA3Ljc5IDBsNTAuNDQgNDkuMjRhLjk3Ljk3IDAgMCAwIDEuMzYgMGw1MC40NC00OS4yNGMyLjE1LTIuMSA1LjY0LTIuMSA3Ljc5IDBsNTAuNDQgNDkuMjRhLjk3Ljk3IDAgMCAwIDEuMzYgMGw1MC40NC00OS4yNGMyLjE1LTIuMSA1LjY0LTIuMSA3Ljc5IDBaIi8+PC9zdmc+'
  readyState = WalletReadyState.Loadable
  supportedTransactionVersions: SupportedTransactionVersions = new Set(['legacy', 0] as const)

  #projectId: string
  #chainId: string
  #onDisplayUri?: (uri: string) => void
  #providerFactory?: WalletConnectSolanaAdapterConfig['providerFactory']

  #provider: WcProviderLike | null = null
  // Shared IN-FLIGHT init promise — concurrent #getProvider() callers (autoConnect + connect) reuse the
  // same UP.init() instead of each starting one, which would spin up two WC cores (#57 double-init).
  #providerPromise: Promise<WcProviderLike> | null = null
  #publicKey: PublicKey | null = null
  #connecting = false
  // Set while a pairing promise from provider.connect() is in flight. Calling it resolves the pending
  // connect() benignly (no session adopted, no 'error' emitted) so the QR modal closing can UNWIND a
  // never-settling pairing. Without it, closing the modal before the user pairs leaves #connecting stuck
  // `true` until the pairing expires (~5 min); the next open then no-ops behind the guard in connect()
  // and the QR never re-renders — the "stalls, need to refresh" bug (#57 follow-up).
  #abortConnect: (() => void) | null = null

  constructor(config: WalletConnectSolanaAdapterConfig) {
    super()
    this.#projectId = config.projectId
    this.#chainId = resolveChainId(config.network)
    this.#onDisplayUri = config.onDisplayUri
    this.#providerFactory = config.providerFactory
  }

  get publicKey(): PublicKey | null {
    return this.#publicKey
  }

  get connecting(): boolean {
    return this.#connecting
  }

  // `connected` is derived from publicKey by the base class (get connected() { return !!this.publicKey }).

  // Lazily initialise UniversalProvider and wire the transport-level listeners exactly once.
  async #getProvider(): Promise<WcProviderLike> {
    if (this.#provider) return this.#provider
    // Memoize the IN-FLIGHT init, not only the resolved provider: autoConnect() and connect() (or a
    // retry) can both reach here before the first UP.init() resolves; without sharing this promise each
    // starts its OWN init → two WalletConnect cores → "No matching key. proposal" → the pairing hangs
    // (connect_hang_suspected / connect_stranded_spinner). One shared init eliminates the double-init.
    if (this.#providerPromise) return this.#providerPromise

    this.#providerPromise = (async (): Promise<WcProviderLike> => {
    const metadata: WcMetadata = {
      name: 'Parse DeFi',
      description: 'Cross-chain USDC bridge & swap aggregator',
      url: typeof window !== 'undefined' ? window.location.origin : 'https://parsedefi.com',
      icons: [
        typeof window !== 'undefined'
          ? `${window.location.origin}/favicon.svg`
          : 'https://parsedefi.com/favicon.svg',
      ],
    }

    let provider: WcProviderLike
    if (this.#providerFactory) {
      provider = (await this.#providerFactory({ projectId: this.#projectId, metadata })) as unknown as WcProviderLike
    } else {
      // Dynamic import keeps the ~heavy WC core out of the initial bundle until the user picks WalletConnect.
      const mod = await import('@walletconnect/universal-provider')
      const UP = mod.default ?? (mod as unknown as { UniversalProvider: typeof UniversalProvider }).UniversalProvider
      provider = (await UP.init({ projectId: this.#projectId, metadata })) as unknown as WcProviderLike
    }

    // Surface the pairing URI so the modal can render the QR (mirrors the EVM WC `display_uri` flow).
    provider.on('display_uri', (...args: unknown[]) => {
      const uri = args[0]
      if (typeof uri !== 'string') return
      this.#onDisplayUri?.(uri)
      // Also emit a custom event so subscribers that hold only the adapter can react. eventemitter3
      // supports arbitrary event names; the typed WalletAdapterEvents union doesn't include 'display_uri'.
      ;(this.emit as (event: string, ...args: unknown[]) => boolean)('display_uri', uri)
    })

    // Remote-initiated teardown (wallet disconnects the session) → clear local state + notify.
    const remoteDisconnect = () => this.#handleRemoteDisconnect()
    provider.on('session_delete', remoteDisconnect)
    provider.on('disconnect', remoteDisconnect)

    this.#provider = provider
    return provider
    })()

    try {
      return await this.#providerPromise
    } catch (err) {
      // A failed init must not stay cached as "in flight" forever — clear it so a later attempt retries.
      this.#providerPromise = null
      throw err
    }
  }

  #adoptSessionAccount(provider: WcProviderLike): PublicKey {
    const account = provider.session?.namespaces?.solana?.accounts?.[0]
    if (!account) throw new WalletConnectionError('WalletConnect session has no Solana account')
    const address = parseSolanaAddressFromCaip(account)
    try {
      return new PublicKey(address)
    } catch (error) {
      throw new WalletPublicKeyError((error as Error)?.message, error)
    }
  }

  async connect(): Promise<void> {
    try {
      if (this.connected || this.#connecting) return
      this.#connecting = true

      let provider: WcProviderLike
      try {
        provider = await this.#getProvider()
      } catch (error) {
        throw new WalletConnectionError((error as Error)?.message, error)
      }

      // If a session was persisted (e.g. relay reconnect), adopt it instead of proposing a new pairing.
      if (provider.session?.namespaces?.solana?.accounts?.length) {
        this.#publicKey = this.#adoptSessionAccount(provider)
        this.emit('connect', this.#publicKey)
        return
      }

      try {
        const pairing = provider.connect({
          namespaces: {
            solana: {
              chains: [this.#chainId],
              methods: [...SOLANA_METHODS],
              events: [],
            },
          },
        })
        // If we abort (modal closed), the original pairing promise keeps running until it expires on the
        // relay; swallow its eventual settlement so it never surfaces as an unhandled rejection.
        pairing.catch(() => {})
        // Race the pairing against an abort hook. A real error/approval settles `pairing` first; a
        // cancelPendingConnect() settles `abort` first → we return without adopting a session or throwing.
        const abort = new Promise<'aborted'>((resolve) => {
          this.#abortConnect = () => resolve('aborted')
        })
        const outcome = await Promise.race([pairing.then(() => 'ok' as const), abort])
        if (outcome === 'aborted') return
      } catch (error) {
        throw new WalletConnectionError((error as Error)?.message, error)
      }

      this.#publicKey = this.#adoptSessionAccount(provider)
      this.emit('connect', this.#publicKey)
    } catch (error) {
      this.emit('error', error as WalletConnectionError)
      throw error
    } finally {
      this.#abortConnect = null
      this.#connecting = false
    }
  }

  // Cancel an in-flight pairing (e.g. the QR modal was closed before the user paired). Safe to call any
  // time — a no-op when nothing is pending. Resets #connecting so the NEXT connect() proposes a fresh
  // pairing (emitting a new display_uri / QR) instead of no-oping behind connect()'s connecting guard.
  cancelPendingConnect(): void {
    this.#abortConnect?.()
  }

  // Called by wallet-adapter-react on page load for the previously-selected wallet. We MUST NOT pop an
  // interactive QR here: only silently restore an already-persisted WC session, otherwise no-op.
  async autoConnect(): Promise<void> {
    try {
      const provider = await this.#getProvider()
      if (!provider.session?.namespaces?.solana?.accounts?.length) return
      this.#publicKey = this.#adoptSessionAccount(provider)
      this.emit('connect', this.#publicKey)
    } catch {
      // Silent — autoConnect must never throw a visible error on load.
    }
  }

  async disconnect(): Promise<void> {
    // Unwind any in-flight pairing first so a hook-level disconnect also clears a stuck #connecting.
    this.#abortConnect?.()
    const provider = this.#provider
    const hasSession = !!provider?.session?.namespaces?.solana?.accounts?.length
    this.#publicKey = null
    // Only tear down a real session — calling provider.disconnect() with no active session throws
    // ("No matching session"), which would surface as a spurious WalletDisconnectionError on cancel.
    if (provider && hasSession) {
      try {
        await provider.disconnect()
      } catch (error) {
        this.emit('error', new WalletDisconnectionError((error as Error)?.message, error))
      }
    }
    this.emit('disconnect')
  }

  #handleRemoteDisconnect(): void {
    if (!this.#publicKey) return
    this.#publicKey = null
    this.emit('disconnect')
  }

  #requireConnected(): { provider: WcProviderLike; publicKey: PublicKey } {
    const provider = this.#provider
    const publicKey = this.#publicKey
    if (!provider || !publicKey) throw new WalletNotConnectedError()
    return { provider, publicKey }
  }

  async signTransaction<T extends Transaction | VersionedTransaction>(transaction: T): Promise<T> {
    try {
      const { provider, publicKey } = this.#requireConnected()
      const versioned = isVersionedTransaction(transaction)

      // Serialize to base64 WITHOUT requiring signatures — the wallet adds the missing signature(s).
      // Legacy: serialize({ requireAllSignatures:false, verifySignatures:false }); Versioned: serialize().
      const rawBytes = versioned
        ? (transaction as VersionedTransaction).serialize()
        : (transaction as Transaction).serialize({ requireAllSignatures: false, verifySignatures: false })
      const transactionB64 = Buffer.from(rawBytes).toString('base64')

      // Route the request to the Solana namespace by passing the CAIP-2 chain id as the 2nd arg.
      const result = await provider.request<{ signature?: string; transaction?: string }>(
        {
          method: 'solana_signTransaction',
          params: { transaction: transactionB64, pubkey: publicKey.toBase58() },
        },
        this.#chainId,
      )

      // Preferred (newer) response: the wallet returns the fully-signed transaction as base64.
      if (result?.transaction) {
        const signedBytes = new Uint8Array(Buffer.from(result.transaction, 'base64'))
        const signed = versioned
          ? (VersionedTransaction.deserialize(signedBytes) as unknown as T)
          : (Transaction.from(signedBytes) as unknown as T)
        return signed
      }

      // Legacy response: only the base58 signature is returned — apply it onto the caller's tx object.
      // DEVICE-TEST-REQUIRED: the versioned-tx branch of this path (signature applied to a
      // VersionedTransaction via addSignature) is the least-exercised; confirm the resulting tx broadcasts.
      if (result?.signature) {
        const sigBytes = bs58.decode(result.signature)
        if (sigBytes.length !== 64) {
          throw new WalletSignTransactionError(
            `solana_signTransaction returned a ${sigBytes.length}-byte signature (expected 64)`,
          )
        }
        if (versioned) {
          ;(transaction as VersionedTransaction).addSignature(publicKey, sigBytes)
        } else {
          ;(transaction as Transaction).addSignature(publicKey, Buffer.from(sigBytes))
        }
        return transaction
      }

      throw new WalletSignTransactionError(
        'solana_signTransaction response contained neither `transaction` nor `signature`',
      )
    } catch (error) {
      if (error instanceof WalletSignTransactionError) {
        this.emit('error', error)
        throw error
      }
      const wrapped = new WalletSignTransactionError((error as Error)?.message, error)
      this.emit('error', wrapped)
      throw wrapped
    }
  }

  async signAllTransactions<T extends Transaction | VersionedTransaction>(transactions: T[]): Promise<T[]> {
    // The WC `solana_signAllTransactions` batch method's request/response encoding is inconsistently
    // implemented across wallets; sign sequentially via the well-specified single-tx method instead.
    // (Base BaseSignerWalletAdapter.signAllTransactions does the same sequential fallback.)
    const signed: T[] = []
    for (const transaction of transactions) {
      signed.push(await this.signTransaction(transaction))
    }
    return signed
  }

  async signMessage(message: Uint8Array): Promise<Uint8Array> {
    try {
      const { provider, publicKey } = this.#requireConnected()

      // Per spec, `message` is base58-encoded; the returned `signature` is base58 → decode to raw bytes.
      const result = await provider.request<{ signature: string }>(
        {
          method: 'solana_signMessage',
          params: { message: bs58.encode(message), pubkey: publicKey.toBase58() },
        },
        this.#chainId,
      )
      if (!result?.signature) {
        throw new WalletSignMessageError('solana_signMessage response had no `signature`')
      }
      const sigBytes = bs58.decode(result.signature)
      if (sigBytes.length !== 64) {
        throw new WalletSignMessageError(
          `solana_signMessage returned a ${sigBytes.length}-byte signature (expected 64)`,
        )
      }
      return sigBytes
    } catch (error) {
      if (error instanceof WalletSignMessageError) {
        this.emit('error', error)
        throw error
      }
      const wrapped = new WalletSignMessageError((error as Error)?.message, error)
      this.emit('error', wrapped)
      throw wrapped
    }
  }
}
